import { DatePipe, NgClass } from '@angular/common'
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { AbstractControl, FormControl, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { EditorComponent } from '@app/modules/shared/components/editor/editor.component'
import { parsePublicDateTimeFromApi } from '@app/v2/event/components/event-dates-and-timings/utils/event-dates-timings.utils'
import { InvitationEmailTemplateModel } from '@app/v2/event/types/invitation-email-template.model'
import {
  InvitationEmailTemplate,
  InvitationEmailTemplateFontSize,
  InvitationEmailTemplateInfo,
  InvitationEmailTemplateTiming,
  InvitationEmailTemplateTimingDay
} from '@app/v2/event/types/invitation-email-template.type'
import { BannerUploadFieldComponent } from '@app/v2/shared/components/banner-upload-field/banner-upload-field.component'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { environment } from '@environments/environment'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
const FONT_FAMILY_OPTIONS_INTERNAL: DropdownOption[] = [
  {
    key: 'arial',
    value: 'Arial',
    data: { fontFamily: 'Arial, Helvetica, sans-serif' }
  },
  {
    key: 'helvetica',
    value: 'Helvetica',
    data: { fontFamily: 'Helvetica, Arial, Verdana, sans-serif' }
  },
  {
    key: 'verdana',
    value: 'Verdana',
    data: { fontFamily: 'Verdana, Geneva, sans-serif' }
  },
  {
    key: 'tahoma',
    value: 'Tahoma',
    data: { fontFamily: 'Tahoma, Geneva, sans-serif' }
  },
  {
    key: 'trebuchet-ms',
    value: 'Trebuchet MS',
    data: { fontFamily: 'Trebuchet MS, Helvetica, sans-serif' }
  },
  {
    key: 'georgia',
    value: 'Georgia',
    data: { fontFamily: 'Georgia, serif' }
  },
  {
    key: 'times-new-roman',
    value: 'Times New Roman',
    data: { fontFamily: 'Times New Roman, Times, serif' }
  },
  {
    key: 'courier-new',
    value: 'Courier New',
    data: { fontFamily: 'Courier New, Courier, monospace' }
  }
]
interface CustomInviteButton {
  id: 'confirm' | 'decline'
  text: string
  textColor: string
  backgroundColor: string
  isBold?: boolean
  isItalic?: boolean
  isUnderline?: boolean
  fontSize?: InvitationEmailTemplateFontSize
  hidden?: boolean
}

const DEFAULT_CUSTOM_BUTTONS: CustomInviteButton[] = [
  { id: 'confirm', ...InvitationEmailTemplateModel.DEFAULT_BUTTONS.confirm },
  { id: 'decline', ...InvitationEmailTemplateModel.DEFAULT_BUTTONS.decline }
]

@Component({
  selector: 'app-custom-template',
  templateUrl: './custom-template.component.html',
  styleUrls: ['./custom-template.component.scss'],
  standalone: true,
  imports: [FormsModule, EditorComponent, DatePipe, NgClass, DropdownComponent, NgxTippyModule, BannerUploadFieldComponent, BasicInputComponent]
})
export class CustomTemplateComponent implements OnInit, OnChanges, OnDestroy {
  /** Event model */
  @Input() event!: InvitationEmailTemplate

  /** Invitation info model */
  @Input() invitationInfo!: InvitationEmailTemplateInfo

  /** Event timings list */
  @Input() eventTimings: InvitationEmailTemplateTiming[] = []

  /** Currently selected persona slug (for pricing-based CTAs if needed later) */
  @Input() selectedPersonaSlug!: string

  /** Event id used by the banner upload API */
  @Input() eventId: number | null = null

  /** Organization / workspace id used by the banner upload API */
  @Input() organizationId: number | null = null

  @ViewChild('editorRef') editorRef!: EditorComponent
  @ViewChild(BannerUploadFieldComponent) private _bannerUploadField?: BannerUploadFieldComponent

  /** Form control bound to the reusable banner upload field */
  imageUrlControl = new FormControl<string>('', { nonNullable: true })

  subjectControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, this._subjectValidator()]
  })

  /** Super admins upload the invitation image as-is (no cropper, no 1200x500 restriction) */
  readonly isSuperAdmin = inject(WorkspaceRoleService).isSuperAdmin()

  /** Two customizable buttons: confirm / decline */
  customButtons: CustomInviteButton[] = []
  /** Which button's color popup is open (null when closed) */
  colorPopupFor: 'confirm' | 'decline' | 'card' | 'location' | 'calendar' | null = null
  /** Which button's font-size dropdown is open inside the popup */
  fontSizeMenuFor: 'confirm' | 'decline' | null = null
  /** Which element is hovered (to show wheel) */
  hoverTarget: 'confirm' | 'decline' | 'card' | 'location' | 'calendar' | null = null

  /** Card (date / location header) background color */
  cardBackgroundColor: string | null = null
  /** Add to Calendar button background color */
  calendarButtonColor: string | null = null

  /** Whether all day rows are expanded in the timings preview. */
  isTimingsExpanded = false

  /**
   * Per-open dropdown options copy.
   * Field dropdown toggles `disabled` on options, so this must be reinitialized on each modal open.
   */
  fontFamilyOptions: DropdownOption[] = []

  /** Font family CSS value (includes fallbacks) */
  fontFamily: string = FONT_FAMILY_OPTIONS_INTERNAL[0].data?.fontFamily

  private static readonly _defaultVisibleDays = 5

  /** Cached day-grouped timings; rebuilt when `eventTimings` changes. */
  private _timingDays: InvitationEmailTemplateTimingDay[] = []

  private _applyFontFamilyTimeoutId: ReturnType<typeof setTimeout> | null = null

  ngOnInit(): void {
    this._syncTimingDays(this.eventTimings)
    this.imageUrlControl.setValue(this.invitationInfo?.imageUrl ?? '')
    this.subjectControl.setValue(this.invitationInfo?.subject?.trim() || InvitationEmailTemplateModel.buildDefaultSubject(this.event?.name))
    this.fontFamily = this._resolveFontFamilyFromSaved(this.invitationInfo?.fontFamily)
    if (this.invitationInfo) {
      this.invitationInfo.fontFamily = this.fontFamily
    }
    this._initCustomButtons()
    this._reinitFontFamilyOptions()
    this._applyFontFamilyToEditor()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventTimings'] && !changes['eventTimings'].firstChange) {
      this._syncTimingDays(changes['eventTimings'].currentValue)
    }
  }

  ngOnDestroy(): void {
    this._clearApplyFontFamilyTimeout()
  }

  /**
   * Rebuilds font dropdown options from source list and marks selected row for current font.
   * This prevents stale dimmed state when the modal is reopened.
   */
  private _reinitFontFamilyOptions(): void {
    this.fontFamilyOptions = FONT_FAMILY_OPTIONS_INTERNAL.map((opt) => ({
      ...opt,
      data: opt.data ? { ...opt.data } : undefined,
      disabled: false
    }))
    const selected = this._findFontFamilyOption(this.fontFamily)
    const selectedOption = this.fontFamilyOptions.find((opt) => opt.key === selected?.key)
    if (selectedOption) selectedOption.disabled = true
  }

  /** Initialize confirm/decline buttons from invitationInfo or defaults. */
  private _initCustomButtons(): void {
    const saved = this.invitationInfo?.customButtons

    if (saved?.confirm && saved?.decline) {
      this.customButtons = [
        { id: 'confirm', ...saved.confirm },
        { id: 'decline', ...saved.decline }
      ]
    } else {
      // Start from defaults, but make confirm reflect price & currency if persona is paid
      this.customButtons = DEFAULT_CUSTOM_BUTTONS.map((b) => ({ ...b }))
      const confirm = this.customButtons.find((b) => b.id === 'confirm')
      if (confirm) confirm.text = this.getDefaultConfirmLabel()
      this._syncTemplateAppearanceToInvitation()
    }

    // Ensure style flags are always initialized (backward compatible with older saved payloads)
    for (const btn of this.customButtons) {
      btn.isBold ??= false
      btn.isItalic ??= false
      btn.isUnderline ??= false
      btn.fontSize = InvitationEmailTemplateModel.toFontSizeCss(btn.fontSize)
    }
    const decline = this.customButtons.find((b) => b.id === 'decline')
    if (decline) decline.hidden ??= false

    // Initialize card/calendar colors from invitationInfo if present; otherwise match what the user sees by default
    this.cardBackgroundColor = this.invitationInfo?.cardBackgroundColor || InvitationEmailTemplateModel.DEFAULT_CARD_BACKGROUND
    this.calendarButtonColor = this.invitationInfo?.calendarButtonColor || InvitationEmailTemplateModel.DEFAULT_CALENDER_BUTTON

    // Ensure invitationInfo starts with the same defaults the user sees
    this._syncTemplateAppearanceToInvitation()
  }

  /**
   * Persist current appearance (buttons + card/calendar colors) into invitationInfo
   * so it goes into the payload.
   */
  private _syncTemplateAppearanceToInvitation(): void {
    if (!this.invitationInfo) return
    this.invitationInfo.customButtons = {
      confirm: this._getButtonConfig('confirm'),
      decline: this._getButtonConfig('decline')
    }
    this.invitationInfo.cardBackgroundColor = this.cardBackgroundColor ?? InvitationEmailTemplateModel.DEFAULT_CARD_BACKGROUND
    this.invitationInfo.calendarButtonColor = this.calendarButtonColor ?? InvitationEmailTemplateModel.DEFAULT_CALENDER_BUTTON
    this.invitationInfo.fontFamily = this.fontFamily
  }

  private _getButtonConfig(id: 'confirm' | 'decline') {
    const button = this.customButtons.find((x) => x.id === id)!
    return {
      text: button.text,
      textColor: button.textColor,
      backgroundColor: button.backgroundColor,
      isBold: !!button.isBold,
      isItalic: !!button.isItalic,
      isUnderline: !!button.isUnderline,
      fontSize: InvitationEmailTemplateModel.toFontSizeCss(button.fontSize),
      ...(id === 'decline' ? { hidden: !!button.hidden } : {})
    }
  }

  /** Shows or hides the decline button. Hidden sends an empty decline label. */
  public setDeclineHidden(hidden: boolean, event?: Event): void {
    event?.stopPropagation()
    const decline = this.declineButton
    if (!decline) return
    decline.hidden = hidden
    if (hidden && (this.colorPopupFor === 'decline' || this.fontSizeMenuFor === 'decline')) {
      this.colorPopupFor = null
      this.fontSizeMenuFor = null
    }
    if (hidden && this.hoverTarget === 'decline') this.hoverTarget = null
    this._syncTemplateAppearanceToInvitation()
  }

  /**
   * Handles blur on CTA span. Restores default "Edit Registration" when empty,
   * same behavior as in Request Revision modal.
   */
  /** Returns current label for a given button. */
  getButtonLabel(id: 'confirm' | 'decline'): string {
    const btn = this.customButtons.find((b) => b.id === id)
    if (!btn) return id === 'confirm' ? this.getDefaultConfirmLabel() : 'Decline'
    return btn.text || (id === 'confirm' ? this.getDefaultConfirmLabel() : 'Decline')
  }

  /** Convenience getters for template binding. */
  get confirmButton(): CustomInviteButton {
    return this.customButtons.find((b) => b.id === 'confirm')!
  }
  get declineButton(): CustomInviteButton {
    return this.customButtons.find((b) => b.id === 'decline')!
  }

  /** Static Google Maps image for the event location */
  get staticMapUrl(): string {
    return InvitationEmailTemplateModel.buildStaticMapUrl(this.event?.location_details, environment.publicGoogleMapsAPIKey)
  }

  /** Venue name for the location card body. */
  get venueName(): string {
    return this.event?.location_details?.venue_name?.trim() || ''
  }

  /** Venue address for the location card body. */
  get venueAddress(): string {
    return this.event?.location_details?.venue_address?.trim() || ''
  }

  /** Resolved Add to Calendar link color (saved value or model default). */
  get resolvedCalendarButtonColor(): string {
    return this.calendarButtonColor || InvitationEmailTemplateModel.DEFAULT_CALENDER_BUTTON
  }

  /** Event start day (`YYYY-MM-DD`) in the event's own calendar. */
  get publicStartDay(): string | Date | null {
    return this._toEventDay(this.event?.public_start_date)
  }

  /** Event end day (`YYYY-MM-DD`) in the event's own calendar. */
  get publicEndDay(): string | Date | null {
    return this._toEventDay(this.event?.public_end_date)
  }

  /** Day rows currently shown (collapsed to first N unless expanded). */
  get displayedEventTimings(): InvitationEmailTemplateTimingDay[] {
    if (this.isTimingsExpanded || this._timingDays.length <= CustomTemplateComponent._defaultVisibleDays) {
      return this._timingDays
    }
    return this._timingDays.slice(0, CustomTemplateComponent._defaultVisibleDays)
  }

  get hiddenTimingsCount(): number {
    return Math.max(0, this._timingDays.length - CustomTemplateComponent._defaultVisibleDays)
  }

  get showTimingsToggle(): boolean {
    return this._timingDays.length > CustomTemplateComponent._defaultVisibleDays
  }

  public toggleTimingsExpanded(): void {
    this.isTimingsExpanded = !this.isTimingsExpanded
  }

  /** Handles blur on confirm/decline input. */
  onButtonBlur(buttonId: 'confirm' | 'decline', event: Event): void {
    const input = event.target as HTMLInputElement
    const content = (input.value || '').trim()
    const btn = this.customButtons.find((b) => b.id === buttonId)
    if (!btn) return

    if (content === '') {
      if (buttonId === 'confirm') {
        btn.text = this.getDefaultConfirmLabel()
      } else {
        const fallback = DEFAULT_CUSTOM_BUTTONS.find((b) => b.id === buttonId)
        btn.text = fallback ? fallback.text : 'Decline'
      }
      input.value = btn.text
    } else {
      btn.text = content
    }
    this._syncTemplateAppearanceToInvitation()
  }

  /**
   * Default confirm label:
   * If selected persona has enable_payment and a registration_amount + currency,
   * show: "Secure your ticket now - (amount CURRENCY)".
   * Otherwise: "Confirm".
   */
  private getDefaultConfirmLabel(): string {
    const forms = this.event?.forms || []
    const form = forms.find((f) => f.slug === this.selectedPersonaSlug)
    if (!form?.enable_payment) return 'Confirm'

    const amount = form.registration_amount ?? this.event?.registration_amount ?? 0
    const currencySlug = (this.event?.currency?.slug || form?.currency?.slug || '').toString().toUpperCase()

    const amountStr = Number(amount).toFixed(2)
    return currencySlug ? `Secure your ticket now - (${amountStr} ${currencySlug})` : `Secure your ticket now - (${amountStr})`
  }

  /** Opens the color-editor popup for a button (wheel click). */
  public openColorPopup(target: 'confirm' | 'decline' | 'card' | 'location' | 'calendar', event: Event): void {
    event.stopPropagation()
    this.colorPopupFor = this.colorPopupFor === target ? null : target
    this.fontSizeMenuFor = null
  }

  /** Closes the color popup and syncs to invitationInfo. */
  public closeColorPopup(): void {
    this.colorPopupFor = null
    this.fontSizeMenuFor = null
    this._syncTemplateAppearanceToInvitation()
  }

  public toggleButtonTextStyle(buttonId: 'confirm' | 'decline', style: 'bold' | 'italic' | 'underline', event?: Event): void {
    event?.stopPropagation()
    const btn = this.customButtons.find((b) => b.id === buttonId)
    if (!btn) return

    if (style === 'bold') btn.isBold = !btn.isBold
    if (style === 'italic') btn.isItalic = !btn.isItalic
    if (style === 'underline') btn.isUnderline = !btn.isUnderline

    this._syncTemplateAppearanceToInvitation()
  }

  public toggleButtonFontSizeMenu(buttonId: 'confirm' | 'decline', event: Event): void {
    event.stopPropagation()
    this.fontSizeMenuFor = this.fontSizeMenuFor === buttonId ? null : buttonId
  }

  public selectButtonFontSize(buttonId: 'confirm' | 'decline', fontSize: InvitationEmailTemplateFontSize, event?: Event): void {
    event?.stopPropagation()
    const btn = this.customButtons.find((b) => b.id === buttonId)
    if (!btn) return

    btn.fontSize = fontSize
    this.fontSizeMenuFor = null
    this._syncTemplateAppearanceToInvitation()
  }

  public getButtonFontSizeLabel(buttonId: 'confirm' | 'decline'): string {
    const btn = this.customButtons.find((b) => b.id === buttonId)
    const fontSize = InvitationEmailTemplateModel.toFontSizeCss(btn?.fontSize)
    if (fontSize === '14px') return 'Small'
    if (fontSize === '18px') return 'Large'
    return 'Normal'
  }

  // input-based editing removed; contenteditable spans now handle text changes via onButtonBlur.

  /**
   * Syncs banner display URL and upload original into invitationInfo for PATCH.
   * Display path → `imageUrl` (`image_url`); upload original → `image_url` field (`original_url`).
   */
  public onBannerChanged(change: { field: string; value: string; original_image?: string }): void {
    if (!this.invitationInfo) return
    const url = (change.value || '').trim()
    this.invitationInfo.imageUrl = url || undefined
    if (!url) {
      this.invitationInfo.image_url = undefined
    } else if (change.original_image !== undefined) {
      const original = (change.original_image || '').trim()
      this.invitationInfo.image_url = original || undefined
    }
  }

  /**
   * True while the banner is uploading/preloading, or the crop modal is open.
   * Uses the banner field's existing public API only (no shared banner behavior changes).
   */
  public isBannerBusy(): boolean {
    const banner = this._bannerUploadField
    if (!banner) return false
    if (banner.showLoadingOverlay()) return true
    return banner.isModalOpened(banner.cropModalId)
  }

  flushEditorToInvitationInfo(): void {
    this.editorRef?.flushInvitationMessageToModel()
    if (this.invitationInfo) {
      const url = (this.imageUrlControl.value || '').trim()
      this.invitationInfo.imageUrl = url || undefined
      if (!url) this.invitationInfo.image_url = undefined
      this.invitationInfo.subject = this.subjectControl.value.trim()
    }
    this._syncTemplateAppearanceToInvitation()
  }

  /**
   * Marks the subject as touched so its error shows, and reports whether the template can be submitted.
   * @returns True when the subject is valid.
   */
  public validateSubject(): boolean {
    this.subjectControl.markAsTouched()
    return this.subjectControl.valid
  }

  /**
   * Rejects whitespace-only subjects and subjects over the email provider limit.
   * Length is checked on the trimmed value because that is what gets saved.
   */
  private _subjectValidator(): ValidatorFn {
    const maxLength = InvitationEmailTemplateModel.SUBJECT_MAX_LENGTH
    return (control: AbstractControl): ValidationErrors | null => {
      const trimmed = String(control.value ?? '').trim()
      if (control.value && !trimmed) return { required: true }
      if (trimmed.length <= maxLength) return null
      return { subjectMaxLength: `The email service limits subjects to ${maxLength} characters. Please shorten it to continue.` }
    }
  }

  public onFontFamilySelected(option: DropdownOption): void {
    if (!option) return
    const cssFont = this._unquotedFontFamily((option.data && option.data.fontFamily) || option.value)
    this.fontFamily = cssFont
    if (this.invitationInfo) {
      this.invitationInfo.fontFamily = this.fontFamily
    }
    // Keep the dimmed row in sync with the selected label.
    this._reinitFontFamilyOptions()
    this._applyFontFamilyToEditor()
  }

  /**
   * Groups flat invitation timings by day for the custom-template preview layout.
   * @param timings - The timings to group by day.
   * @returns The grouped timings.
   */
  private _syncTimingDays(timings: InvitationEmailTemplateTiming[] | null | undefined): void {
    this._timingDays = InvitationEmailTemplateModel.groupTimingsByDay(timings)
    this.isTimingsExpanded = false
  }

  /**
   * API public dates are event wall-clock time, so keep only the date part to avoid browser timezone shifts.
   * @param value - Raw `public_start_date` / `public_end_date`.
   * @returns `YYYY-MM-DD`, the original Date, or null.
   */
  private _toEventDay(value: string | Date | null | undefined): string | Date | null {
    if (typeof value !== 'string') return value ?? null
    return parsePublicDateTimeFromApi(value).date || null
  }

  /** Strips quotes from saved font-family stacks so payloads stay unescaped. */
  private _unquotedFontFamily(fontFamily: string | undefined): string {
    if (!fontFamily) return ''
    return String(fontFamily).replace(/"/g, '').trim()
  }

  /**
   * Finds a known font dropdown option for a saved/current CSS stack, label, or primary name.
   */
  private _findFontFamilyOption(fontFamily: string | undefined): DropdownOption | undefined {
    const unquoted = this._unquotedFontFamily(fontFamily)
    if (!unquoted) return undefined

    const exact = FONT_FAMILY_OPTIONS_INTERNAL.find((opt) => opt.data?.fontFamily === unquoted)
    if (exact) return exact

    const byLabel = FONT_FAMILY_OPTIONS_INTERNAL.find((opt) => opt.value.toLowerCase() === unquoted.toLowerCase())
    if (byLabel) return byLabel

    const primary = unquoted.split(',')[0].trim().toLowerCase()
    return FONT_FAMILY_OPTIONS_INTERNAL.find((opt) => {
      const optionPrimary = (opt.data?.fontFamily || '').split(',')[0].trim().toLowerCase()
      return optionPrimary === primary || opt.value.toLowerCase() === primary
    })
  }

  /**
   * Maps a saved `font_family` value to a known editor CSS stack.
   * Accepts full stacks, primary family names, or dropdown labels from the API.
   */
  private _resolveFontFamilyFromSaved(savedFont: string | undefined): string {
    return this._findFontFamilyOption(savedFont)?.data?.fontFamily || this._unquotedFontFamily(savedFont) || InvitationEmailTemplateModel.DEFAULT_FONT_FAMILY
  }

  private _clearApplyFontFamilyTimeout(): void {
    if (this._applyFontFamilyTimeoutId == null) return
    clearTimeout(this._applyFontFamilyTimeoutId)
    this._applyFontFamilyTimeoutId = null
  }

  private _applyFontFamilyToEditor(retryCount = 0): void {
    if (!this.fontFamily) return

    const qlEditor = this.editorRef?.editor?.quillEditor?.root as HTMLElement | undefined

    // Editor can mount after template init; retry briefly so initial selection always applies.
    if (!qlEditor) {
      if (retryCount < 5) {
        this._clearApplyFontFamilyTimeout()
        this._applyFontFamilyTimeoutId = setTimeout(() => this._applyFontFamilyToEditor(retryCount + 1), 100)
      }
      return
    }

    this._clearApplyFontFamilyTimeout()
    qlEditor.style.setProperty('font-family', this.fontFamily, 'important')
    qlEditor.querySelectorAll<HTMLElement>('*').forEach((element) => {
      element.style.setProperty('font-family', this.fontFamily, 'important')
    })
  }

  public getSelectedFontFamilyLabel(): string {
    return this._findFontFamilyOption(this.fontFamily)?.value ?? FONT_FAMILY_OPTIONS_INTERNAL[0].value
  }
}
